import {
  Flex,
  Box,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { SpacerWidgetOptions, WidgetType } from '../shared/types';
import { Icons } from '@texas/components/shared/Icons';
import { VerifyDialogWithFunction } from '@texas/components/shared/dialog/VerifyDialogWithFunction';
import { EditWidget } from '../shared/components/EditWidget';
import { WidgetFactoryContext } from '../useWidgetLayout';
import { useActiveContext } from '@texas/hooks/useActiveContext';

interface Props {
  widgetId: string;
  options: SpacerWidgetOptions | undefined;
}

export function SpacerWidget({ widgetId, options }: Props) {
  const { t } = useTranslation();
  const { removeWidget, updateWidget } = useActiveContext(WidgetFactoryContext);
  const {
    isOpen: isRemoveOpen,
    onClose: onRemoveClose,
    onOpen: onRemoveOpen,
  } = useDisclosure();
  const {
    isOpen: isEditOpen,
    onClose: onEditClose,
    onOpen: onEditOpen,
  } = useDisclosure();

  const resolvedAlignment = options?.alignment ?? 'center';
  const resolvedFontSize = options?.fontSize ?? 14;
  const resolvedTextColor = options?.textColor;

  return (
    <>
      <Box
        className="header"
        position="relative"
        height="100%"
        bg="transparent"
        borderRadius={8}
        transition="background-color 200ms ease"
        cursor="grab"
        _active={{ cursor: 'grabbing' }}
        role="group"
        _hover={{
          bg: 'whiteAlpha.50',
          _light: { bg: 'gray.50' },
        }}
        sx={{
          '& .resize-handle': {
            opacity: 0,
            transition: 'opacity 200ms ease',
          },
          '&:hover .resize-handle': {
            opacity: 1,
          },
        }}
      >
        {options?.customWidgetTitle && (
          <Flex h="100%" w="100%" alignItems="center">
            <Text
              as="pre"
              fontFamily="inherit"
              fontSize={`${resolvedFontSize}px`}
              fontWeight="medium"
              color={resolvedTextColor ?? 'whiteAlpha.600'}
              _light={{ color: resolvedTextColor ?? 'gray.400' }}
              pointerEvents="none"
              userSelect="none"
              textAlign={resolvedAlignment}
              maxH="100%"
              w="100%"
              whiteSpace="break-spaces"
              overflow="auto"
            >
              {options.customWidgetTitle}
            </Text>
          </Flex>
        )}
        <Box
          className="spacer-menu ignore"
          position="absolute"
          top="50%"
          left="50%"
          _groupHover={{ opacity: 1 }}
          opacity={0}
          transform="translate(-50%, -50%)"
          zIndex={10}
        >
          <Menu isLazy={true}>
            <MenuButton
              as={IconButton}
              size="sm"
              variant="solid"
              colorScheme="gray"
              aria-label="Spacer settings"
              icon={<Icons.DotsHorizontal boxSize="5" />}
            />
            <Portal>
              <MenuList>
                <MenuItem
                  onClick={onEditOpen}
                  icon={<Icons.Pencil boxSize={5} />}
                >
                  {t('widgets.edit')}
                </MenuItem>
                <MenuItem
                  onClick={onRemoveOpen}
                  icon={<Icons.Trash boxSize={5} />}
                >
                  {t('widgets.remove')}
                </MenuItem>
              </MenuList>
            </Portal>
          </Menu>
        </Box>

        <CustomResizeHandle />
      </Box>

      <EditWidget
        type={WidgetType.Spacer}
        name={options?.customWidgetTitle ?? ''}
        color={null}
        options={options}
        onUpdate={(_name, _color, updatedOptions) =>
          updateWidget(
            widgetId,
            updatedOptions?.customWidgetTitle ?? '',
            null,
            updatedOptions,
          )
        }
        isOpen={isEditOpen}
        onClose={onEditClose}
      />

      <VerifyDialogWithFunction
        headerTitle={t('general.remove')}
        secondaryButtonTitle={t('general.cancel')}
        primaryButtonTitle={t('general.confirm')}
        toPerform={() => removeWidget(widgetId)}
        isOpen={isRemoveOpen}
        onClose={onRemoveClose}
      >
        {t('alert.areYouSure')}
      </VerifyDialogWithFunction>
    </>
  );
}

const CustomResizeHandle = React.forwardRef<SVGSVGElement, any>(
  function CustomResizeHandle(props, ref) {
    const { handleAxis: _handleAxis, ...restProps } = props;
    return (
      <Icons.ResizeBottomRight
        className="resize-handle react-resizable-handle react-resizable-handle-se"
        pos="absolute"
        right={2}
        bottom={2}
        cursor="se-resize"
        pointerEvents="auto"
        {...restProps}
        ref={ref}
      />
    );
  },
);
